"use client";

import ScriptPreview, {
  ScriptPreviewSkeleton,
} from "@/components/script/script-preview";
import Particles from "@/components/home/particles";
import { Badge } from "@/components/ui/badge";
import { Medal } from "lucide-react";
import { env } from "@/env";
import { useQuery } from "@tanstack/react-query";
import type { Script } from "@/types/script";

interface PaginatedScriptsResponse {
  message: string;
  data: {
    posts: {
      result: Script[];
      total: number;
    };
  };
}

export default function TrendingList() {
  const fetchTrendingScripts = async () => {
    const response = await fetch(
      `${env.NEXT_PUBLIC_API_URL}/public/most-viewed?limit=12`, // `${env.NEXT_PUBLIC_API_URL}/public/most-viewed?limit=12
    );
    if (!response.ok) throw new Error("Failed to fetch trending scripts");
    return response.json() as Promise<PaginatedScriptsResponse>;
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["trending-scripts"],
    queryFn: fetchTrendingScripts,
  });

  const trendingScripts = data?.data.posts.result ?? [];
  const topThreeScripts = trendingScripts.slice(0, 3);
  const restOfScripts = trendingScripts.slice(3);

  return (
    <main>
      <section className="lg:pt-18 pt-24">
        {/* Gradients */}
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-[100px] sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary via-[#98c0ef] to-secondary opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <div
          className="absolute inset-x-0 -z-10 transform-gpu overflow-hidden blur-[100px]"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-primary to-[#98c0ef] opacity-10 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
        <Particles
          className="absolute inset-0 -z-10"
          quantity={100}
          ease={80}
          refresh
        />

        <div className="z-10 mx-auto flex max-w-7xl flex-col items-center justify-center px-6 py-20 lg:px-8 lg:py-32">
          <div className="max-w-2xl items-center justify-center text-center">
            <Badge variant="accent">
              <Medal className="mr-1 inline-block" size={14} />
              Most popular
            </Badge>
            <h1 className="font-heading text-gradient mt-2 text-balance pb-1 text-4xl font-semibold tracking-tight lg:text-5xl">
              Trending Scripts.
            </h1>
            <p className="mt-4 text-balance text-lg leading-tight text-muted-foreground">
              Discover the latest and greatest scripts for your favorite Roblox
              games. Explore new scripts and find the perfect one for you.
            </p>
          </div>

          {isLoading ? (
            <div className="mt-12 grid w-full grid-cols-1 gap-x-1.5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {/* Top three skeletons with numbered badges */}
              {[0, 1, 2].map((index) => (
                <div
                  key={index}
                  className="relative rounded-md border border-primary/10 bg-white/10 p-4 shadow-md"
                >
                  <div className="absolute -top-3 right-1/2 translate-x-1/2">
                    <Badge variant="accent" className="backdrop-blur">
                      {index + 1}
                    </Badge>
                  </div>
                  <div className="pt-1.5">
                    <ScriptPreviewSkeleton />
                  </div>
                </div>
              ))}
              {/* Rest of skeletons */}
              {Array.from({ length: 9 }).map((_, index) => (
                <div
                  key={index + 3}
                  className="rounded-md bg-white/10 p-4 shadow-md"
                >
                  <ScriptPreviewSkeleton />
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-12 grid w-full grid-cols-1 gap-x-1.5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              {topThreeScripts.map((script, index) => (
                <div
                  key={index}
                  className="relative rounded-md border border-primary/10 bg-white/10 p-4 shadow-md"
                >
                  <div className="absolute -top-3 right-1/2 translate-x-1/2">
                    <Badge variant="accent" className="backdrop-blur">
                      {index + 1}
                    </Badge>
                  </div>
                  <div className="pt-1.5">
                    <ScriptPreview {...script} index={index} />
                  </div>
                </div>
              ))}
              {restOfScripts.map((script, index) => (
                <div
                  key={index}
                  className="rounded-md bg-white/10 p-4 shadow-md"
                >
                  <ScriptPreview {...script} index={index} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
